// src/components/ScanVideosModal.jsx — v0.207.0
// ----------------------------------------------------------------------
// Brett: "I have taken some test video for floor scan. I dint have a plan at
// the time. Can you add a spot where i can select the videos from my device and
// save it on the job for processing. Save me getting out a chord and
// transferring."
//
// Pick clips from the phone's camera roll; they go STRAIGHT to storage on a
// short-lived write ticket and land on the job. The three-step sequence and
// every message in it live in utils/scanVideoUpload, which is tested; this file
// is the screen around them.
//
// TWO THINGS THIS DELIBERATELY DOES NOT DO:
//   • It does not need a plan. He shot this footage before there was one ("I
//     dint have a plan at the time"), so a screen that demanded one would throw
//     away the very clips it exists to keep.
//   • It does not hide a failure. A clip that did not finish shows as PENDING
//     or FAILED in the list, with what to do. See shared/blob.js v0.130.0 for
//     the four months when a failed upload looked exactly like a successful
//     one.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { theme } from '../theme';
import { Video, X, Trash2, AlertTriangle, CheckCircle2, RotateCw, Copy, Download } from 'lucide-react';
import { apiGet, apiSend } from '../api';
import { fmtBytes } from '../utils/scanVideoUpload';
import { scanVideoQueue } from '../utils/scanVideoQueue';

const STAGE_TEXT = {
  checking: 'Checking…',
  ticket: 'Starting…',
  uploading: 'Uploading',
  confirming: 'Finishing…',
  done: 'Saved',
};

export default function ScanVideosModal({ projectId, reportId, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [queue, setQueue] = useState([]);       // [{key, name, size, stage, pct, error, detail}]
  const [checking, setChecking] = useState({});  // { [id]: true } while storage is being asked
  const [notes, setNotes] = useState([]);       // plain statements of fact, not errors
  const [dropHover, setDropHover] = useState(false);
  const dragDepth = useRef(0);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await apiGet(`/api/scan-videos?projectId=${encodeURIComponent(projectId)}`, { fresh: true });
      setRows(Array.isArray(r) ? r : []);
      setErr(null);
    } catch (e) {
      setErr((e && (e.error || e.message)) || 'Could not load the videos on this job.');
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // v0.207.4 — THE QUEUE IS NOT THIS SCREEN'S ANY MORE.
  //
  // Brett: "can these uploads work in the back ground so i can close the screen
  // and it still uploads?" They could not, and the reason was structural: the
  // queue was state inside this component, so closing the picker unmounted the
  // only thing that knew an upload was happening. Half a floor scan died every
  // time somebody tapped the X, silently.
  //
  // It lives in utils/scanVideoQueue now, above every screen. This component
  // SUBSCRIBES to it, and closing it changes nothing about what is in flight.
  const q = scanVideoQueue();
  useEffect(() => q.subscribe(setQueue), [q]);
  useEffect(() => q.subscribeNotes(setNotes), [q]);

  // A clip finishing is a row appearing in the list below, so the list is
  // reloaded when the queue empties out.
  const inFlight = queue.filter(x => x.stage !== 'error').length;
  const prevInFlight = useRef(inFlight);
  useEffect(() => {
    if (prevInFlight.current > 0 && inFlight === 0) load();
    prevInFlight.current = inFlight;
  }, [inFlight, load]);

  const onFiles = useCallback((files) => {
    if (!files || !files.length) return;
    q.add(files, { projectId, reportId });
  }, [q, projectId, reportId]);

  const retry = useCallback((entry) => { if (entry && entry.file) q.retry(entry.key); }, [q]);
  const retryAll = useCallback(() => q.retryAll(), [q]);

  // v0.207.2 — ASK STORAGE, DON'T RE-UPLOAD.
  //
  // Brett: "videos say did not finish uploading - add it again. Even though i
  // saw it get to 100%". A clip whose bytes DID land but whose confirmation did
  // not is a row sitting at pending over a perfectly good file, and telling
  // somebody on 4G to send two hundred megabytes again to find that out is the
  // wrong answer. The API confirms against storage itself, so this is the same
  // call the upload makes — it either finds the file and finishes the job, or
  // says plainly that it is not there.
  const verify = useCallback(async (row) => {
    setErr(null);
    setChecking(c => ({ ...c, [row.id]: true }));
    try {
      const updated = await apiSend('PATCH', `/api/scan-videos/${row.id}`, { status: 'ready' });
      setRows(rs => rs.map(r => (r.id === row.id ? updated : r)));
    } catch (e) {
      if (e && e.blobMissing) {
        try { await apiSend('PATCH', `/api/scan-videos/${row.id}`, { status: 'failed' }); } catch (_) { /* the message below is the point */ }
        await load();
      }
      setErr((e && (e.error || e.message)) || 'Could not check that video.');
    } finally {
      setChecking(c => { const n = { ...c }; delete n[row.id]; return n; });
    }
  }, [load]);

  const remove = useCallback(async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete “${row.name || row.originalName || 'this video'}”? The file is removed from the job and from storage.`)) return;
    try {
      await apiSend('DELETE', `/api/scan-videos/${row.id}`);
      setRows(rs => rs.filter(r => r.id !== row.id));
    } catch (e) {
      setErr((e && (e.error || e.message)) || 'Could not delete that video.');
    }
  }, []);

  const lbl = { fontSize: 12, color: theme.textLight };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
      onClick={onClose}
    >
      <div
        data-surface-card
        onClick={(e) => e.stopPropagation()}
        style={{ background: theme.surface, color: theme.text, width: 'min(680px, 100%)', maxHeight: '90vh', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Floor scan videos</h3>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 0, cursor: 'pointer', color: theme.textLight }}><X size={18} /></button>
        </div>

        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
          onDragEnter={(e) => { e.preventDefault(); dragDepth.current += 1; if (dragDepth.current === 1) setDropHover(true); }}
          onDragLeave={(e) => { e.preventDefault(); dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDropHover(false); }}
          onDrop={(e) => { e.preventDefault(); dragDepth.current = 0; setDropHover(false); onFiles(e.dataTransfer?.files); }}
          style={{
            margin: 14, border: `2px dashed ${theme.brand}`, borderRadius: 10, padding: '20px 14px',
            background: dropHover ? theme.bg : 'transparent', textAlign: 'center', cursor: 'pointer',
          }}
          title="Pick clips from this device"
        >
          <div style={{ marginBottom: 4, color: theme.brand }}><Video size={22} /></div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{dropHover ? 'Release to add' : 'Choose videos from this device'}</div>
          <div style={{ ...lbl, marginTop: 4 }}>
            Straight from the camera roll — no cable. Several at a time is fine; they upload one after another.
          </div>
          <input
            ref={fileRef} type="file" accept="video/*" multiple style={{ display: 'none' }}
            onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }}
          />
        </div>

        {err && (
          <div style={{ margin: '0 14px 10px', padding: '6px 10px', background: theme.bg, color: theme.danger, border: `1px solid ${theme.danger}`, borderRadius: 6, fontSize: 12 }}>
            {err}
          </div>
        )}

        {notes.map(n => (
          <div key={n.id} style={{ margin: '0 14px 10px', padding: '6px 10px', background: theme.bg, color: theme.textLight, border: `1px solid ${theme.border}`, borderRadius: 6, fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <Copy size={13} style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ flex: 1 }}>{n.text}</span>
            <button onClick={() => q.clearNote(n.id)} style={{ background: 'none', border: 0, color: theme.textLight, cursor: 'pointer', padding: 0 }}><X size={13} /></button>
          </div>
        ))}

        <div style={{ overflowY: 'auto', flex: 1, padding: '0 14px 14px' }}>
          {/* IN FLIGHT */}
          {queue.map(q => (
            <div key={q.key} style={{ padding: '8px 10px', border: `1px solid ${q.stage === 'error' ? theme.danger : theme.border}`, borderRadius: 8, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.name}</span>
                <span style={lbl}>{q.stage === 'error' ? 'Not saved' : STAGE_TEXT[q.stage] || ''}{q.stage === 'uploading' && q.pct != null ? ` ${q.pct}%` : ''}</span>
              </div>
              {q.stage === 'uploading' && (
                <div style={{ height: 4, background: theme.bg, borderRadius: 2, marginTop: 6, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${q.pct || 0}%`, background: theme.brand, transition: 'width .2s' }} />
                </div>
              )}
              {q.stage === 'error' && (
                <>
                  <div style={{ ...lbl, color: theme.danger, marginTop: 4 }}>
                    <AlertTriangle size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                    {q.error}
                    {q.detail ? <span style={{ opacity: 0.7 }}> ({q.detail})</span> : null}
                  </div>
                  {/* v0.207.3 — THE BUTTON HE ASKED FOR. "You probably should
                      juust have a try again button." A filename is not an
                      instruction on a phone, where the camera roll shows
                      thumbnails and no names; the clip he picked is still in
                      the browser's hands, so the app does the finding. */}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    {q.file && (
                      <button
                        onClick={() => retry(q)}
                        style={{ flex: 1, minHeight: 40, fontSize: 14, fontWeight: 700, color: '#fff', background: theme.brand, border: 0, borderRadius: 8, cursor: 'pointer' }}
                      >
                        <RotateCw size={15} style={{ verticalAlign: -3, marginRight: 6 }} />Try again
                      </button>
                    )}
                    <button
                      onClick={() => scanVideoQueue().dismiss(q.key)}
                      style={{ minHeight: 40, padding: '0 14px', fontSize: 14, fontWeight: 600, color: theme.textLight, background: 'none', border: `1px solid ${theme.border}`, borderRadius: 8, cursor: 'pointer' }}
                    >
                      Dismiss
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}

          {/* More than one failed: one tap for the lot, rather than a tap each
              while standing in a kitchen holding a phone. */}
          {queue.filter(x => x.stage === 'error' && x.file).length > 1 && (
            <button
              onClick={retryAll}
              style={{ width: '100%', minHeight: 44, marginBottom: 8, fontSize: 14, fontWeight: 700, color: '#fff', background: theme.brand, border: 0, borderRadius: 8, cursor: 'pointer' }}
            >
              <RotateCw size={15} style={{ verticalAlign: -3, marginRight: 6 }} />
              Try all {queue.filter(x => x.stage === 'error' && x.file).length} again
            </button>
          )}

          {/* SAVED */}
          {loading && !rows.length ? (
            <div style={{ padding: 20, textAlign: 'center', ...lbl }}>Loading…</div>
          ) : !rows.length && !queue.length ? (
            <div style={{ padding: 20, textAlign: 'center', ...lbl }}>
              No videos on this job yet. A floor scan does not need a plan — add the clips now and tie them to a plan later.
            </div>
          ) : rows.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: `1px solid ${theme.border}`, borderRadius: 8, marginBottom: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.name || r.originalName || `Video ${r.id}`}
                  {/* v0.207.3 — the copies he ended up with, named as copies.
                      The API flags the LATER one, so what is marked is always
                      safe to delete and the original stays. */}
                  {r.duplicateOfId ? (
                    <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: theme.warn || theme.textLight, border: `1px solid ${theme.border}`, borderRadius: 4, padding: '1px 5px' }}>
                      copy
                    </span>
                  ) : null}
                </div>
                <div style={lbl}>
                  {r.status === 'ready' ? (
                    <><CheckCircle2 size={12} style={{ verticalAlign: -2, marginRight: 4, color: theme.success || theme.brand }} />
                      {fmtBytes(r.sizeBytes)}{r.recordedAt ? ` · filmed ${new Date(r.recordedAt).toLocaleString()}` : ''}</>
                  ) : r.status === 'failed' ? (
                    <span style={{ color: theme.danger }}>
                      <AlertTriangle size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                      Not in storage — it is still on your phone, add it again.
                    </span>
                  ) : (
                    <span style={{ color: theme.warn || theme.textLight }}>
                      Still uploading, or it finished without being confirmed. Check storage before adding it again.
                    </span>
                  )}
                </div>
              </div>
              {r.status !== 'ready' && (
                <button
                  onClick={() => verify(r)}
                  disabled={!!checking[r.id]}
                  style={{ fontSize: 12, fontWeight: 600, color: theme.brand, background: 'none', border: `1px solid ${theme.border}`, borderRadius: 6, padding: '4px 8px', cursor: checking[r.id] ? 'default' : 'pointer' }}
                >
                  {checking[r.id] ? 'Checking…' : 'Check storage'}
                </button>
              )}
              {r.url && (
                <a href={r.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: theme.brand, textDecoration: 'none', fontWeight: 600 }}>View</a>
              )}
              {/* v0.207.6 — Brett: "I cant load it off from my phone". The View
                  link PLAYS the clip; a phone browser frequently offers no way
                  to get the file itself out of a player, and the HTML download
                  attribute cannot help because storage is another origin. The
                  API signs a second URL that Azure serves as an attachment. */}
              {r.downloadUrl && (
                <a
                  href={r.downloadUrl}
                  style={{ fontSize: 12, color: theme.textLight, textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  title="Save this clip to the device"
                >
                  <Download size={14} />Save
                </a>
              )}
              <button onClick={() => remove(r)} aria-label="Delete" title="Delete" style={{ background: 'none', border: 0, cursor: 'pointer', color: theme.danger }}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
